import React from 'react';
import { DeliveryHistory, DeliveryHistoryResponse } from '../types/deliveryHistory.types';
import { DeliveryCountTotals } from '@/shared/utils/aggregationUtils';
import { useTableSort } from '@/shared/hooks/useTableSort';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import Pagination from '@/shared/components/dataDisplay/Pagination';

const COLUMNS = [
    { key: 'suppName', label: '협력사' },
    { key: 'riderName', label: '이름' },
    { key: 'deliveryStatusName', label: '운행상태' },
    { key: 'finishCnt', label: '완료' },
    { key: 'rejectCnt', label: '거절' },
    { key: 'assingCancelCnt', label: '배차취소' },
    { key: 'deliCancelCnt', label: '배달취소' },
    { key: 'morTimeCnt', label: '아침점심피크' },
    { key: 'afterNonCnt', label: '오후논피크' },
    { key: 'nightNonCnt', label: '저녁피크시간' },
    { key: 'nightCnt', label: '심야논피크' },
    { key: 'riderId', label: '아이디' },
    { key: 'phoneNum', label: '휴대폰번호' },
] as const;

interface DeliveryTableProps {
    deliveryHistoryData: DeliveryHistoryResponse;
    page: number;
    limit: number;
    totals: DeliveryCountTotals;
    pageTotals: DeliveryCountTotals;
    renderOperationStatus: (statusName?: string) => React.ReactNode;
    onPageChange: (page: number) => void;
    onRowClick: (row: DeliveryHistory) => void;
}

const DeliveryTable: React.FC<DeliveryTableProps> = ({
                                                         deliveryHistoryData,
                                                         page,
                                                         limit,
                                                         totals,
                                                         pageTotals,
                                                         renderOperationStatus,
                                                         onPageChange,
                                                         onRowClick,
                                                     }) => {
    const { sortConfig, sortedData, handleSort } = useTableSort<DeliveryHistory>({
        data: deliveryHistoryData?.data || [],
        numericColumns: [
            'finishCnt',
            'rejectCnt',
            'assingCancelCnt',
            'deliCancelCnt',
            'morTimeCnt',
            'afterNonCnt',
            'nightNonCnt',
            'nightCnt',
        ],
    });

    return (
        <div className="card">
            <div className="card-body">
                {deliveryHistoryData?.data && deliveryHistoryData.data.length > 0 ? (
                    <>
                        <div className="table-container">
                            <table className="delivery-table sortable-table">
                                <thead>
                                <tr>
                                    {COLUMNS.map((col) => {
                                        const isSorted = sortConfig.columnKey === col.key;
                                        const direction = isSorted ? sortConfig.direction : null;
                                        return (
                                            <th
                                                key={col.key}
                                                className={`sortable-header ${isSorted ? 'sorted' : ''}`}
                                                onClick={() => handleSort(col.key)}
                                            >
                                                {col.label}
                                                <SortIcon direction={direction} />
                                            </th>
                                        );
                                    })}
                                </tr>
                                </thead>
                                <tbody>
                                <tr className="total-row">
                                    <td></td>
                                    <td>총 합계</td>
                                    <td>-</td>
                                    <td>{totals.finishCnt.toLocaleString()}</td>
                                    <td>{totals.rejectCnt.toLocaleString()}</td>
                                    <td>{totals.assingCancelCnt.toLocaleString()}</td>
                                    <td>{totals.deliCancelCnt.toLocaleString()}</td>
                                    <td>{totals.morTimeCnt.toLocaleString()}</td>
                                    <td>{totals.afterNonCnt.toLocaleString()}</td>
                                    <td>{totals.nightNonCnt.toLocaleString()}</td>
                                    <td>{totals.nightCnt.toLocaleString()}</td>
                                    <td></td>
                                    <td></td>
                                </tr>
                                <tr className="page-total-row">
                                    <td></td>
                                    <td>페이지별 합계</td>
                                    <td>-</td>
                                    <td>{pageTotals.finishCnt.toLocaleString()}</td>
                                    <td>{pageTotals.rejectCnt.toLocaleString()}</td>
                                    <td>{pageTotals.assingCancelCnt.toLocaleString()}</td>
                                    <td>{pageTotals.deliCancelCnt.toLocaleString()}</td>
                                    <td>{pageTotals.morTimeCnt.toLocaleString()}</td>
                                    <td>{pageTotals.afterNonCnt.toLocaleString()}</td>
                                    <td>{pageTotals.nightNonCnt.toLocaleString()}</td>
                                    <td>{pageTotals.nightCnt.toLocaleString()}</td>
                                    <td></td>
                                    <td></td>
                                </tr>
                                {sortedData.map((row: DeliveryHistory, idx: number) => (
                                    <tr
                                        key={`${row.supplyId}-${row.riderId}-${idx}`}
                                        className="clickable-row"
                                        onClick={() => onRowClick(row)}
                                    >
                                        <td>{row.suppName}</td>
                                        <td>{row.riderName}</td>
                                        <td>{renderOperationStatus(row.deliveryStatusName)}</td>
                                        <td>{row.finishCnt.toLocaleString()}</td>
                                        <td>{row.rejectCnt.toLocaleString()}</td>
                                        <td>{row.assingCancelCnt.toLocaleString()}</td>
                                        <td>{row.deliCancelCnt.toLocaleString()}</td>
                                        <td>{row.morTimeCnt.toLocaleString()}</td>
                                        <td>{row.afterNonCnt.toLocaleString()}</td>
                                        <td>{row.nightNonCnt.toLocaleString()}</td>
                                        <td>{row.nightCnt.toLocaleString()}</td>
                                        <td>{row.riderId}</td>
                                        <td>{row.phoneNum ?? '-'}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {deliveryHistoryData.total > limit && (
                            <div className="pagination-container">
                                <Pagination
                                    currentPage={page}
                                    totalPages={Math.ceil(deliveryHistoryData.total / limit)}
                                    onPageChange={onPageChange}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="empty-state">
                        <i className="fas fa-inbox empty-state-icon" />
                        <h3>배달 내역이 없습니다</h3>
                        <p>검색 조건에 맞는 배달 내역이 없습니다.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeliveryTable;
